import { useSyncExternalStore } from 'react';

export const CHAT_WALLPAPERS = ['aurora', 'dunes', 'forest', 'ocean', 'dusk', 'blueprint'] as const;
export type ChatWallpaper = typeof CHAT_WALLPAPERS[number] | 'none';
export type ChatSurface = 'chat' | 'members';
export interface ChatSurfacePrefs { chat: ChatWallpaper; members: ChatWallpaper }
const KEY = 'bunker.chat-surfaces.v1';
const DEFAULTS: ChatSurfacePrefs = { chat: 'none', members: 'none' };
let cache: ChatSurfacePrefs | undefined;
const listeners = new Set<() => void>();

function wallpaper(value: unknown): ChatWallpaper {
  return CHAT_WALLPAPERS.find((id) => id === value) ?? 'none';
}
function load(): ChatSurfacePrefs {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    if (!value || typeof value !== 'object') return { ...DEFAULTS };
    return {
      chat: wallpaper('chat' in value ? value.chat : undefined),
      members: wallpaper('members' in value ? value.members : undefined),
    };
  } catch {
    // Storage can be unavailable in private/restricted browser contexts.
    return { ...DEFAULTS };
  }
}
export function getChatSurfacePrefs(): ChatSurfacePrefs { return cache ??= load(); }
function emit() { listeners.forEach((fn) => fn()); }
function onStorage(event: StorageEvent) {
  if (event.key !== KEY && event.key !== null) return;
  cache = load();
  emit();
}
function subscribe(listener: () => void) {
  if (!listeners.size) window.addEventListener('storage', onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (!listeners.size) window.removeEventListener('storage', onStorage);
  };
}
export function setChatWallpaper(target: ChatSurface, value: ChatWallpaper) {
  const next = wallpaper(value);
  if (getChatSurfacePrefs()[target] === next) return;
  cache = { ...getChatSurfacePrefs(), [target]: next };
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch {
    // Keep the selected design for this session even if persistence is blocked.
  }
  emit();
}
export function useChatSurfacePrefs() {
  return useSyncExternalStore(subscribe, getChatSurfacePrefs, getChatSurfacePrefs);
}
export function _resetChatSurfacesForTest() { cache = undefined; }
